"use client";

import {
  OptimizedTooltip,
  OptimizedTooltipContent,
  OptimizedTooltipTrigger,
} from "../ui/optimized-tooltip";
import { cn } from "@erp/shared-libs/utils";
import Image, { ImageProps } from "next/image";
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { env } from "next-runtime-env";
import { GenerateS3CacheHashKey } from "@erp/frontend-api/services";

// 使用動態的 image base url
// 這樣在不同環境可以使用不同的 CDN
// 例如開發環境使用本地的 image proxy，正式環境使用真正的 CDN
const imageBaseUrl = env("NEXT_PUBLIC_IMAGE_BASE_URL") as string;

type LoadingImageProps = Omit<ImageProps, "src"> & {
  src?: string;
  containerClassName?: string;
  containerStyle?: React.CSSProperties;
  loadClassName?: string;
  missingImageClassName?: string;
};

export const LoadingImage: FC<LoadingImageProps> = memo(
  ({
    alt,
    loadClassName,
    containerClassName,
    missingImageClassName,
    containerStyle,
    className,
    src,
    ...props
  }) => {
    // =========================================================
    // Hooks
    // =========================================================
    const previousSrc = useRef<string | undefined>(undefined);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isReloading, setIsReloading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [useFallback, setUseFallback] = useState(false);

    // =========================================================
    // Variables
    // =========================================================
    const isMissingImage = useMemo(() => !src, [src]);
    const isLoadError = useMemo(() => isError, [isError]);

    /**
     * 佔位 icon 的寬度：取容器寬度的 1/3，並上限，避免大圖時 icon 過大
     */
    const iconWidth = useMemo(() => Math.min(Number(props.width) / 3, 24), [props.width]);

    /**
     * 佔位 icon 的高度：取容器高度的 1/3，並上限 40px，避免大圖時 icon 過大
     */
    const iconHeight = useMemo(() => Math.min(Number(props.height) / 3, 24), [props.height]);

    // =========================================================
    // Functions
    // =========================================================
    const handleOnLoad = useCallback(() => {
      setIsLoaded(true);
      setIsReloading(false);
    }, []);

    const handleOnError = useCallback(() => {
      if (!useFallback) {
        // 第一次失敗，切換到 API route
        setUseFallback(true);
        setIsError(false);
        setIsLoaded(false);
      } else {
        // API route 也失敗，顯示錯誤
        setIsError(true);
      }
    }, [useFallback]);

    const imageLoader = useCallback(
      ({ src, width, quality }: { src: string; width: number; quality?: number }) => {
        const isPublicSrc = src.startsWith("/images/");
        if (isPublicSrc) {
          // 如果是我們預期的公共圖片路徑，直接返回原始 URL，讓瀏覽器處理
          return src;
        }

        if (useFallback) {
          // 使用 API route 作為 fallback
          const q = quality || 75;
          return `/media/image?url=${encodeURIComponent(src)}&w=${width}&q=${q}`;
        } else {
          // 先嘗試直接 CDN URL
          const w = width || 800;
          const q = quality || 75;
          const key = GenerateS3CacheHashKey(src, w, q);
          return `${imageBaseUrl}/${key}`;
        }
      },
      [useFallback]
    );

    // =========================================================
    // Effects
    // =========================================================
    // 圖片切換時重置狀態
    useEffect(() => {
      if (previousSrc.current && previousSrc.current !== src) {
        setIsReloading(true);
      }
      previousSrc.current = src;
    }, [src]);

    // =========================================================
    // Render
    // =========================================================
    return (
      // 這邊要給他寬高，這樣 image 才會正常 load
      // 因為在 loaded 以前 image 會被設置為 absolute，避免佔住 loading 占位符的空間
      // 如果沒有給寬高，因為 lazy load 的關係，圖片永遠不會被看到，所以不會被載入
      // 也需要設置 maxWidth，否則圖片會超出容器寬度
      <div
        className={cn("relative flex h-full w-full justify-center rounded-md", containerClassName)}
        style={{
          width: props.width,
          ...containerStyle,
        }}
      >
        {isLoaded ? null : (
          <div className={cn("relative flex aspect-square flex-1 items-center", loadClassName)}>
            <div
              className={cn(
                `bg-primary/10 relative flex flex-1 items-center justify-center self-stretch overflow-hidden
                  rounded-md`,
                isMissingImage && "border-primary/40 border border-dashed",
                isMissingImage && missingImageClassName,
                !isMissingImage &&
                  !isLoadError &&
                  `before:skeleton before:absolute before:inset-0 before:from-white/0 before:via-white/20
                    before:to-white/0`
              )}
            >
              {isLoadError && (
                <OptimizedTooltip>
                  <OptimizedTooltipTrigger>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={iconWidth}
                      height={iconHeight}
                      viewBox="0 0 256 256"
                      className="fill-primary/40"
                    >
                      <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16h64a8,8,0,0,0,7.59-5.47l14.83-44.48L163,151.43a8.07,8.07,0,0,0,4.46-4.46l14.62-36.55,44.48-14.83A8,8,0,0,0,232,88V56A16,16,0,0,0,216,40ZM117,152.57a8,8,0,0,0-4.62,4.9L98.23,200H40V160.69l46.34-46.35a8,8,0,0,1,11.32,0l32.84,32.84Zm115-30.84V200a16,16,0,0,1-16,16H137.73a8,8,0,0,1-7.59-10.53l7.94-23.8a8,8,0,0,1,4.61-4.9l35.77-14.31,14.31-35.77a8,8,0,0,1,4.9-4.61l23.8-7.94A8,8,0,0,1,232,121.73Z"></path>
                    </svg>
                  </OptimizedTooltipTrigger>
                  <OptimizedTooltipContent>
                    <p className="max-w-75 break-all text-center text-xs">
                      載圖片失敗：
                      <br />
                      {alt}
                    </p>
                  </OptimizedTooltipContent>
                </OptimizedTooltip>
              )}
              {isMissingImage && (
                <OptimizedTooltip>
                  <OptimizedTooltipTrigger>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={iconWidth}
                      height={iconHeight}
                      fill="currentColor"
                      className="fill-primary/40"
                      viewBox="0 0 16 16"
                    >
                      <path d="M.002 3a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2zm1 9v1a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062zm5-6.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0" />
                    </svg>
                  </OptimizedTooltipTrigger>
                  <OptimizedTooltipContent>
                    <p className="max-w-75 break-all text-center text-xs">
                      無圖片：
                      <br />
                      {alt}
                    </p>
                  </OptimizedTooltipContent>
                </OptimizedTooltip>
              )}
              {!isLoadError && !isMissingImage && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={iconWidth}
                  height={iconHeight}
                  fill="currentColor"
                  className="fill-primary/40"
                  viewBox="0 0 16 16"
                >
                  <path d="M.002 3a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2zm1 9v1a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062zm5-6.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0" />
                </svg>
              )}
            </div>
          </div>
        )}
        {src ? (
          <Image
            alt={alt}
            src={src}
            loader={imageLoader}
            onLoad={handleOnLoad}
            onError={handleOnError}
            className={cn("rounded-md object-cover", className, {
              "h-0! w-0! invisible absolute left-0 top-0": !isLoaded,
            })}
            {...props}
          />
        ) : null}

        {isReloading && !isMissingImage ? (
          <div
            className={cn(
              `before:skeleton before:absolute before:inset-0 before:from-white/0 before:via-white/20
                before:to-white/0`,
              "bg-background/50 absolute left-0 top-0 z-10 grid h-full w-full place-content-center"
            )}
          />
        ) : null}
      </div>
    );
  }
);

LoadingImage.displayName = "LoadingImage";
